// RefreshFilesModal.tsx — files popup for refresh mode (KAN-1464 Task 8).
// Add/remove semantics are immediate against the product's real file scope.
// Uploads use the shared V6 sequential queue so multi-file picks are supported
// and one failure does not block remaining files.
import React, { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { NPGlyph, NPIcon } from "./atoms";
import { addProductFile, removeProductFile } from "../productRefresh/files";
import {
    UploadQueueList,
    useSequentialFileUploadQueue,
    type SequentialFileUploadQueueLifecycleHandlers
} from "@/components/fileUploadQueue";
import useFileUpload, { type FileExtractionEvent } from "@/hooks/useFileUpload";

const ACCEPTED_FILE_TYPES = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.html,.json,.md";

export interface RefreshProductFile {
    fileId: number;
    fileName: string;
}

export function RefreshFilesModal({ files, token, projectId, onFilesUpdated, onChanged, onClose }: {
    files: RefreshProductFile[];
    token: string;
    projectId: number;
    /** Keeps the parent Files chip in sync after every successful add/remove. */
    onFilesUpdated: (files: RefreshProductFile[]) => void;
    /** Called once with the final file list, only if >=1 add/remove succeeded, when the modal closes. */
    onChanged: (files: RefreshProductFile[]) => void;
    onClose: () => void;
}) {
    const [list, setList] = useState<RefreshProductFile[]>(files);
    const [removingId, setRemovingId] = useState<number | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const changedRef = useRef(false);
    const listRef = useRef<RefreshProductFile[]>(files);
    const lifecycleHandlersRef = useRef<SequentialFileUploadQueueLifecycleHandlers | null>(null);
    const closeRequestedRef = useRef(false);
    const closedRef = useRef(false);
    const onChangedSentRef = useRef(false);
    const startedAssociationsRef = useRef(0);
    const settledAssociationsRef = useRef(0);
    const settleTargetAtCloseRef = useRef<number | null>(null);

    const maybeEmitOnChanged = () => {
        if (!closeRequestedRef.current) return;
        if (settleTargetAtCloseRef.current == null) return;
        if (settledAssociationsRef.current < settleTargetAtCloseRef.current) return;
        if (!changedRef.current) return;
        if (onChangedSentRef.current) return;
        onChangedSentRef.current = true;
        onChanged(listRef.current);
    };

    const updateList = (next: RefreshProductFile[]) => {
        listRef.current = next;
        setList(next);
        onFilesUpdated(next);
    };

    const updateListIfMissing = (added: RefreshProductFile) => {
        if (listRef.current.some(file => file.fileId === added.fileId)) return;
        updateList([...listRef.current, added]);
    };

    const { handleUpload } = useFileUpload({
        onUploadProgress: progress => lifecycleHandlersRef.current?.onUploadProgress(progress),
        onSavingFileToDatabaseStart: () => lifecycleHandlersRef.current?.onSavingFileToDatabaseStart(),
        onFileTextExtractionEvent: (_fileName: string, event: FileExtractionEvent) => {
            lifecycleHandlersRef.current?.onFileTextExtractionEvent(event);
        },
        onError: message => {
            lifecycleHandlersRef.current?.onUploadError(message);
        }
    });

    const scopeUploadedFile = async (fileId: number, file: File) => {
        startedAssociationsRef.current += 1;
        try {
            const added = await addProductFile(token, projectId, fileId, file.name);
            changedRef.current = true;
            updateListIfMissing(added);
        } catch (error) {
            console.error("Failed to scope file to product", error);
            toast.error(`Could not add ${file.name} to this product - try again.`);
        } finally {
            settledAssociationsRef.current += 1;
            maybeEmitOnChanged();
        }
    };

    const queue = useSequentialFileUploadQueue({
        handleUpload,
        onFileSucceeded: ({ fileId, file }) => {
            void scopeUploadedFile(fileId, file);
        },
        onFileFailed: ({ error }) => {
            if (/\b401\b/.test(error)) toast.error("Session expired - please retry.");
            else toast.error(error || "Upload failed");
        },
        registerLifecycleHandlers: handlers => {
            lifecycleHandlersRef.current = handlers;
        }
    });

    const handlePick = (event: React.ChangeEvent<HTMLInputElement>) => {
        const picked = event.target.files ? Array.from(event.target.files) : [];
        event.target.value = "";
        queue.enqueueFiles(picked);
    };

    const handleRemove = async (fileId: number, fileName: string) => {
        setRemovingId(fileId);
        try {
            await removeProductFile(token, projectId, fileId);
            changedRef.current = true;
            updateList(listRef.current.filter(file => file.fileId !== fileId));
        } catch (error) {
            console.error("Failed to remove product file", error);
            toast.error(`Could not remove ${fileName} — try again.`);
        } finally {
            setRemovingId(null);
        }
    };

    const handleClose = () => {
        if (closedRef.current) return;
        closeRequestedRef.current = true;
        settleTargetAtCloseRef.current = startedAssociationsRef.current;
        maybeEmitOnChanged();
        closedRef.current = true;
        onClose();
    };

    const uploadRows = useMemo(
        () => queue.rows.filter(row => row.stage !== "indexed"),
        [queue.rows]
    );
    const recentlyIndexedRows = useMemo(
        () => queue.rows.filter(row => row.stage === "indexed").slice(-3),
        [queue.rows]
    );

    return (
        <div
            style={{
                position: "fixed", inset: 0, zIndex: 60, background: "rgba(15,23,42,0.42)",
                display: "flex", alignItems: "center", justifyContent: "center"
            }}
            onClick={handleClose}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    width: 470, maxHeight: "86%", overflow: "hidden auto",
                    background: "var(--bg-paper)", borderRadius: 16,
                    boxShadow: "0 24px 70px rgba(15,23,42,0.35)", padding: "20px 24px 22px"
                }}
            >
                {/* header — mirrors FilesModal.tsx / ConnectSourceModal */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                    <span style={{
                        width: 38, height: 38, borderRadius: 11, border: "1px solid var(--divider)",
                        display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto"
                    }}>
                        <NPGlyph name="files" size={20} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 16, fontWeight: 650 }}>Files</div>
                        <div style={{ fontSize: 12, color: "var(--fg3)" }}>
                            {list.length === 0 ? "No files linked" : `${list.length} file${list.length === 1 ? "" : "s"} linked`}
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        title="Close"
                        aria-label="Close files"
                        style={{
                            width: 28, height: 28, borderRadius: 9, border: "1px solid var(--divider)",
                            background: "transparent", cursor: "pointer", display: "inline-flex",
                            alignItems: "center", justifyContent: "center", flex: "0 0 auto"
                        }}
                    >
                        <NPIcon name="x" size={12} sw={2.2} color="var(--fg3)" />
                    </button>
                </div>

                <div style={{
                    fontSize: 11.5, color: "var(--np-warn, #d97706)", background: "rgba(217,119,6,0.08)",
                    borderRadius: 9, padding: "8px 10px", marginBottom: 14
                }}>
                    Changes here apply immediately to this product and may trigger a rebuild.
                </div>

                {list.length === 0 ? (
                    <div style={{ fontSize: 12.5, color: "var(--fg3)", marginBottom: 14 }}>
                        No files linked to this product yet. Upload specs, personas, or design docs — your own
                        documents become ground truth the AI can quote.
                    </div>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
                        {list.map(f => (
                            <div
                                key={f.fileId}
                                style={{
                                    display: "flex", alignItems: "center", gap: 10,
                                    padding: "9px 12px", borderRadius: 11, background: "var(--bg-input)"
                                }}
                            >
                                <NPGlyph name="files" size={15} />
                                <span style={{
                                    flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 600,
                                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
                                }}>
                                    {f.fileName}
                                </span>
                                <button
                                    type="button"
                                    aria-label={`Remove ${f.fileName}`}
                                    disabled={removingId === f.fileId}
                                    onClick={() => void handleRemove(f.fileId, f.fileName)}
                                    style={{
                                        width: 24, height: 24, borderRadius: 8, border: "1px solid var(--divider)",
                                        background: "transparent", display: "inline-flex", alignItems: "center",
                                        justifyContent: "center", flex: "0 0 auto",
                                        cursor: removingId === f.fileId ? "default" : "pointer",
                                        opacity: removingId === f.fileId ? 0.55 : 1
                                    }}
                                >
                                    <NPIcon name="x" size={10} sw={2.2} color="var(--fg3)" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <input
                    ref={inputRef}
                    type="file"
                    hidden
                    multiple
                    accept={ACCEPTED_FILE_TYPES}
                    onChange={handlePick}
                    data-testid="np-refresh-files-input"
                />
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    style={{
                        height: 30, padding: "0 12px", borderRadius: 9, fontSize: 12, fontWeight: 600,
                        cursor: "pointer",
                        opacity: 1,
                        fontFamily: "inherit", background: "var(--np-brand)", border: "none", color: "#fff",
                        boxShadow: "0 2px 8px rgba(39,51,194,0.3)",
                        display: "inline-flex", alignItems: "center", gap: 7
                    }}
                >
                    <NPIcon name="plus" size={12} sw={2.4} color="#fff" />
                    Upload file
                </button>
                <UploadQueueList rows={uploadRows} onRemove={queue.removeQueuedRow} onRetry={queue.retryRow} />

                {recentlyIndexedRows.length > 0 && (
                    <div style={{ marginTop: 12, fontSize: 11.5, color: "var(--fg4)" }}>
                        Uploaded: {recentlyIndexedRows.map(row => row.name).join(", ")}
                    </div>
                )}

                <div style={{ fontSize: 11.5, color: "var(--fg4)", marginTop: 10 }}>
                    Uploaded files link to this product immediately.
                </div>
            </div>
        </div>
    );
}
